/**
 * Keeping a room's BASE rent/deposit aligned with the tenant's agreed figures
 * (COL-360).
 *
 * The tenant profile used to write only `bookings.monthly_rent`, so the room —
 * which is what the listing, the room tabs and every property view show — kept
 * its old price. AdminTenants' rent editor already did this sync inline; the
 * arithmetic lives here so the two surfaces cannot drift into billing different
 * amounts for the same edit.
 *
 * Only decisions live in this module. The callers own their Supabase plumbing,
 * which is what makes any of this testable.
 */

import {
  effectiveRentForMonthEUR,
  monthStart,
  type DiscountBookingFields,
} from "./rent-discount";
import { getExpectedRentCentsForDueCalendarMonth, roundRentEUR } from "./rent";

/** Cents difference below which two amounts count as the same figure. */
const CENT_EPSILON = 1;

export interface RoomBasePatch {
  price?: number;
  deposit_amount?: number;
}

/**
 * The room patch a base-value edit implies, or null when the room already
 * agrees.
 *
 * Re-saving the same amount deliberately still repairs a drifted room: that is
 * how an earlier missed sync gets fixed, and it is the behaviour AdminTenants'
 * `dbRoomPriceDrift` check already relied on.
 */
export function resolveRoomBaseSync(params: {
  currentRoomPrice: number | null | undefined;
  currentRoomDeposit: number | null | undefined;
  nextBaseRent: number | null | undefined;
  nextBaseDeposit: number | null | undefined;
}): RoomBasePatch | null {
  const patch: RoomBasePatch = {};

  const nextRent = Number(params.nextBaseRent);
  if (Number.isFinite(nextRent) && nextRent > 0) {
    const current = Number(params.currentRoomPrice);
    const differs =
      !Number.isFinite(current) || Math.abs(nextRent - current) >= 0.02;
    if (differs) patch.price = roundRentEUR(nextRent);
  }

  const nextDeposit = Number(params.nextBaseDeposit);
  if (Number.isFinite(nextDeposit) && nextDeposit > 0) {
    const current = Number(params.currentRoomDeposit);
    const differs =
      !Number.isFinite(current) || Math.abs(nextDeposit - current) >= 0.02;
    if (differs) patch.deposit_amount = roundRentEUR(nextDeposit);
  }

  return Object.keys(patch).length > 0 ? patch : null;
}

export interface RentRowLike {
  /** payments.id — a uuid. */
  id: string;
  due_date: string | null;
  amount_cents: number | null;
}

export interface RentRowReprice {
  id: string;
  amountCents: number;
}

/**
 * Expected rent (cents) for one due calendar month, after base rent, the
 * pro-rata/override move-in-month rule, and any discount window — the single
 * figure every rent write (reprice an existing row, or create a new one)
 * should charge for that month. Discount is expressed per month ("€50 off per
 * month"), so it comes off this month's charge as-is rather than being
 * pro-rated a second time in the move-in month.
 */
export function expectedRentCentsForMonth(params: {
  /** The base rent — not the discounted amount. */
  fullMonthlyRent: number;
  moveInIso: string | null | undefined;
  firstMonthOverride: number | null | undefined;
  dueYear: number;
  dueMonthIndex0: number;
  /** Discount fields of the booking; pass the values being saved, not the stale row. */
  discount?: DiscountBookingFields | null;
}): number {
  const {
    fullMonthlyRent,
    moveInIso,
    firstMonthOverride,
    dueYear,
    dueMonthIndex0,
  } = params;

  const baseCents = getExpectedRentCentsForDueCalendarMonth({
    fullMonthlyRent,
    moveInIso,
    firstMonthOverride,
    dueYear,
    dueMonthIndex0,
  });
  if (!(Number(fullMonthlyRent) > 0)) return baseCents;

  const due = `${dueYear}-${String(dueMonthIndex0 + 1).padStart(2, "0")}-01`;
  const discountBooking: DiscountBookingFields = {
    ...(params.discount ?? {}),
    monthly_rent: fullMonthlyRent,
  };
  const offEUR =
    fullMonthlyRent -
    effectiveRentForMonthEUR(discountBooking, monthStart(due) ?? due);
  return Math.max(0, baseCents - Math.round(offEUR * 100));
}

/**
 * New amounts for rent rows after a base-rent change or a discount edit.
 *
 * Callers must pass UNPAID rows only. A paid row's amount is a record of money
 * that actually arrived; rewriting it would falsify the ledger.
 *
 * Each row is priced from its own due month, so the pro-rated move-in month
 * keeps its pro-rata figure and a discount applies only to the months its
 * window covers. Rows already holding the right amount are omitted, so a
 * caller can write exactly what changed.
 */
export function repriceRentRows(params: {
  rows: RentRowLike[];
  /** The base rent being saved — not the discounted amount. */
  fullMonthlyRent: number;
  moveInIso: string | null | undefined;
  firstMonthOverride: number | null | undefined;
  /** Discount fields of the booking; pass the values being saved, not the stale row. */
  discount?: DiscountBookingFields | null;
}): RentRowReprice[] {
  const { rows, fullMonthlyRent, moveInIso, firstMonthOverride, discount } =
    params;
  if (!(Number(fullMonthlyRent) > 0)) return [];

  const out: RentRowReprice[] = [];

  for (const row of rows || []) {
    const due = String(row.due_date ?? "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) continue;

    const dueYear = Number(due.slice(0, 4));
    const dueMonthIndex0 = Number(due.slice(5, 7)) - 1;

    const nextCents = expectedRentCentsForMonth({
      fullMonthlyRent,
      moveInIso,
      firstMonthOverride,
      dueYear,
      dueMonthIndex0,
      discount,
    });

    if (Math.abs(Number(row.amount_cents ?? 0) - nextCents) < CENT_EPSILON) {
      continue;
    }
    out.push({ id: row.id, amountCents: nextCents });
  }

  return out;
}
